import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Chip, IconButton, Collapse, Button } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import { ExpandMore, ExpandLess, CompareArrows } from '@mui/icons-material';

/**
 * VersionTimeline Component
 * 
 * Displays a visual timeline of version history for rotations or timecards
 * with expandable change details and version comparison capability.
 * 
 * @param {string} entityType - "rotation" or "timecard"
 * @param {number} entityId - Database ID of the entity
 * @param {Array} versions - Array of version objects from API
 * @param {function} onCompare - Callback for version comparison (optional)
 */
const VersionTimeline = ({ entityType, entityId, versions = [], onCompare }) => {
  const navigate = useNavigate();
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [selectedVersions, setSelectedVersions] = useState([]);

  // Toggle version details expansion
  const handleExpandClick = (versionId) => {
    setExpandedVersion(expandedVersion === versionId ? null : versionId);
  };

  // Handle version selection for comparison
  const handleVersionSelect = (version) => {
    if (selectedVersions.some(v => v.id === version.id)) {
      // Deselect
      setSelectedVersions(selectedVersions.filter(v => v.id !== version.id));
    } else if (selectedVersions.length < 2) {
      // Select (max 2)
      setSelectedVersions([...selectedVersions, version]);
    } else {
      // Replace oldest selection
      setSelectedVersions([selectedVersions[1], version]);
    }
  };

  // Trigger comparison when 2 versions selected
  const handleCompare = () => {
    if (selectedVersions.length !== 2) return;

    const [ver1, ver2] = selectedVersions;
    const number1 = ver1.rotation_number || ver1.timecard_id;
    const number2 = ver2.rotation_number || ver2.timecard_id;

    // Construct slugs for navigation
    // Use version_letter if available (e.g., 'A', 'B'), otherwise use 'v' + version_number (e.g., 'v1', 'v2')
    const suffix1 = ver1.version_letter || `v${ver1.version_number}`;
    const suffix2 = ver2.version_letter || `v${ver2.version_number}`;
    const slug1 = `${number1}-${suffix1}`;
    const slug2 = `${number2}-${suffix2}`;
    const type = ver1.rotation_number ? 'rotation' : 'timecard';
    
    console.log('🔍 COMPARE NAVIGATION DEBUG:', {
      ver1: { number: number1, letter: ver1.version_letter, num: ver1.version_number, suffix: suffix1, slug: slug1 },
      ver2: { number: number2, letter: ver2.version_letter, num: ver2.version_number, suffix: suffix2, slug: slug2 },
      type,
      finalUrl: `/compare?type=${type}&v1=${slug1}&v2=${slug2}`
    });
    
    navigate(`/compare?type=${type}&v1=${slug1}&v2=${slug2}`);
  };

  // Get version type color
  const getVersionColor = (versionType) => {
    switch (versionType) {
      case 'schedule':
      case 'initial':
        return 'primary';
      case 'reroute':
      case 'correction':
        return 'warning';
      case 'actual':
      case 'final':
        return 'success';
      default:
        return 'default';
    }
  };

  // Get version type label
  const getVersionTypeLabel = (versionType) => {
    return versionType?.toUpperCase() || 'UNKNOWN';
  };

  // Format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!versions || versions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No version history available for this {entityType}.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Comparison toolbar */}
      {selectedVersions.length > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2">
              Selected for comparison: {selectedVersions.length}/2
            </Typography>
            {selectedVersions.map(v => (
              <Chip
                key={v.id}
                label={`v${v.version_number}`}
                size="small"
                onDelete={() => handleVersionSelect(v)}
              />
            ))}
            {selectedVersions.length === 2 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<CompareArrows />}
                onClick={handleCompare}
              >
                Compare
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Timeline */}
      <Timeline position="right">
        {versions.map((version, index) => {
          const isExpanded = expandedVersion === version.id;
          const isSelected = selectedVersions.some(v => v.id === version.id);
          const isLatest = index === 0; // Assuming sorted by date desc

          return (
            <TimelineItem key={version.id}>
              {/* Left side: Date/Time */}
              <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                <Typography variant="caption">
                  {formatDateTime(version.created_at)}
                </Typography>
              </TimelineOppositeContent>

              {/* Center: Timeline dot */}
              <TimelineSeparator>
                <TimelineDot
                  color={getVersionColor(version.version_type)}
                  variant={isLatest ? 'filled' : 'outlined'}
                />
                {index < versions.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              {/* Right side: Version content */}
              <TimelineContent>
                <Card
                  sx={{
                    mb: 2,
                    border: isSelected ? 2 : 0,
                    borderColor: 'primary.main',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleVersionSelect(version)}
                >
                  <CardContent>
                    {/* Version header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">
                        Version {version.version_number}
                      </Typography>
                      
                      {isLatest && (
                        <Chip
                          label="CURRENT"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      
                      <Chip
                        label={getVersionTypeLabel(version.version_type)}
                        size="small"
                        color={getVersionColor(version.version_type)}
                      />
                    </Box>

                    {/* Change summary */}
                    {version.change_summary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {version.change_summary}
                      </Typography>
                    )}

                    {/* Expand/collapse button */}
                    {version.changes && version.changes.length > 0 && (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandClick(version.id);
                          }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                        <Typography variant="caption" color="text.secondary">
                          {version.changes.length} change{version.changes.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}

                    {/* Expandable change details */}
                    <Collapse in={isExpanded} timeout="auto">
                      <Box sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                        {version.changes?.map((change, idx) => (
                          <Box key={idx} sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {change.field_name}:
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {' '}{change.old_value} → {change.new_value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>
  );
};

export default VersionTimeline;
